import {
    AuthenticationDetails, CognitoAccessToken, CognitoIdToken, CognitoRefreshToken,
    CognitoUser,
    CognitoUserAttribute,
    CognitoUserPool, CognitoUserSession
} from 'amazon-cognito-identity-js';

const userPoolId = process.env['REACT_APP_USERPOOL_ID'];
const clientId = process.env['REACT_APP_CLIENT_ID'];
const cognitoDomain = process.env['REACT_APP_COGNITO_DOMAIN'];
const cognitoLogoutUrl = cognitoDomain ? `https://${cognitoDomain}/logout` : undefined;
const cognitoTokenUrl = cognitoDomain ? `https://${cognitoDomain}/oauth2/token` : undefined;


const poolData = {
  UserPoolId: `${userPoolId}`,
  ClientId: `${clientId}`,
};

const userPool: CognitoUserPool = new CognitoUserPool(poolData);

let currentUser: CognitoUser | null = userPool.getCurrentUser();

export function getCurrentUser(): CognitoUser | null {
  return currentUser;
}


let idToken: string | undefined = undefined;

export const refreshIdToken = async (): Promise<string | undefined> => {
    try {
        const session: any = await getSession();
        idToken = session.idToken.jwtToken;
    } catch (err) {
        idToken = undefined;
    }
    return idToken;
};

export const getIdToken = (): string | undefined => {
    return idToken;
};


function getCognitoUser(username: string) {
  const userData = {
    Username: username,
    Pool: userPool,
  };
  const cognitoUser = new CognitoUser(userData);
  cognitoUser.setAuthenticationFlowType("USER_PASSWORD_AUTH");

  return cognitoUser;
}

export async function getSession() {
  if (!currentUser) {
    currentUser = userPool.getCurrentUser();
  }

  return new Promise(function(resolve, reject) {
    if (currentUser !== null) {
        currentUser.getSession(function(err: any, session: any) {
        if (err) {
            reject(err);
        } else {
            resolve(session);
        }
        });
    } else {
        resolve(null);
    }
  }).catch((err) => {
    throw err;
  });
}

export async function signUpUserWithEmail(username: string, email: string, password: string) {
  return new Promise(function(resolve, reject) {
    const attributeList = [
      new CognitoUserAttribute({
        Name: 'email',
        Value: email,
      }),
    ];

    userPool.signUp(username, password, attributeList, [], function(err, res) {
      if (err) {
        reject(err);
      } else {
        resolve(res);
      }
    });
  }).catch((err) => {
    throw err;
  });
}

export async function verifyCode(username: string, code: string) {
  return new Promise(function(resolve, reject) {
    const cognitoUser = getCognitoUser(username);

    cognitoUser.confirmRegistration(code, true, function(err, result) {
      if (err) {
        reject(err);
      } else {
        resolve(result);
      }
    });
  }).catch((err) => {
    throw err;
  });
}

export function signInWithCode(code: string): Promise<void> {
    if(!clientId || !cognitoTokenUrl) {
        return Promise.reject("Missing Cognito configuration.");
    }

    const data = new URLSearchParams({
        grant_type: "authorization_code",
        code: code,
        redirect_uri: `${window.location.origin}/loginCallback`,
        client_id: clientId
    });

    return fetch(`${cognitoTokenUrl}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data
    }).then(async (response) => {
        if (!response.ok) {
            throw new Error(`Failed to fetch token: ${response.statusText}`);
        }
        return response.json();
    }).then(authResult => {
        if(authResult.id_token === undefined || authResult.access_token === undefined || authResult.refresh_token === undefined) {
            throw new Error("Missing token in response.");
        }

        const idToken = new CognitoIdToken({
            IdToken: authResult.id_token,
        });
        const accessToken = new CognitoAccessToken({
            AccessToken: authResult.access_token,
        });
        const refreshToken = new CognitoRefreshToken({
            RefreshToken: authResult.refresh_token,
        });

        const sessionData = {
            IdToken: idToken,
            AccessToken: accessToken,
            RefreshToken: refreshToken,
        };

        const userSession = new CognitoUserSession(sessionData);
        const email = idToken.payload["email"];
        if(!email) {
            throw new Error("Missing email in token.");
        }
        const newUser = new CognitoUser({Username: email, Pool: userPool});
        newUser.setSignInUserSession(userSession);
        currentUser = newUser;
    });
}

export async function signInWithEmail(username: string, password: string) {
  return new Promise(function(resolve, reject) {
    const authenticationData = {
      Username: username,
      Password: password,
    };
    const authenticationDetails = new AuthenticationDetails(authenticationData);

    currentUser = getCognitoUser(username);

    currentUser.authenticateUser(authenticationDetails, {
      onSuccess: function(res: any) {
        resolve(res);
      },
      onFailure: function(err: any) {
        reject(err);
      },
      newPasswordRequired: () => {
        reject({
            code: "NEW_PASSWORD_REQUIRED",
            message: "You must reset your password."
        });
      }
    });
  }).catch((err) => {
    throw err;
  });
}

export async function signInWithPasswordReset(username: string, oldPassword: string, newPassword: string) {
  return new Promise(function(resolve, reject) {
    const authenticationData = {
      Username: username,
      Password: oldPassword,
    };
    const authenticationDetails = new AuthenticationDetails(authenticationData);

    currentUser = getCognitoUser(username);

    currentUser.authenticateUser(authenticationDetails, {
      onSuccess: function(res: any) {
        resolve(res);
      },
      onFailure: function(err: any) {
        reject(err);
      },
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        // the api doesn't accept this field back
        delete userAttributes.email_verified;
        currentUser?.completeNewPasswordChallenge(newPassword, requiredAttributes,  {
            onSuccess: (user) => {
              resolve(user);
            },
            onFailure: (error) => {
              reject(error);
            },
          });
      }
    });
  }).catch((err) => {
    throw err;
  });
}

export function signOut(): void {
  if (currentUser) {
    currentUser.signOut();
  }
}

export function getLogoutUrl(): string {
    const logoutUri = `${window.location.origin}/login`;
    return `${cognitoLogoutUrl}?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
}

export async function sendCodeForgotPassword(username: string) {
  return new Promise(function(resolve, reject) {
    const cognitoUser = getCognitoUser(username);

    if (!cognitoUser) {
      reject(`could not find ${username}`);
      return;
    }

    cognitoUser.forgotPassword({
      onSuccess: function(res) {
        resolve(res);
      },
      onFailure: function(err) {
        reject(err);
      },
    });
  }).catch((err) => {
    throw err;
  });
}

export async function confirmPassword(username: string, code: string, password: string) {
  return new Promise(function(resolve, reject) {
    const cognitoUser = getCognitoUser(username);

    if (!cognitoUser) {
      reject(`could not find ${username}`);
      return;
    }

    return cognitoUser.confirmPassword(code, password, {
      onSuccess: function() {
        resolve('password updated');
      },
      onFailure: function(err) {
        reject(err);
      },
    });
  });
}

export async function changePassword(oldPassword: string, newPassword: string) {
  return new Promise(function(resolve, reject) {
    currentUser?.changePassword(oldPassword, newPassword, function(err: any, res: any) {
      if (err) {
        reject(err);
      } else {
        resolve(res);
      }
    });
  });
}

export async function resendCode(username: string) {
    return new Promise(function(resolve, reject) {
        const cognitoUser = getCognitoUser(username);

        if (!cognitoUser) {
          reject(`could not find ${username}`);
          return;
        }

        cognitoUser.resendConfirmationCode(function(err: any, res: any) {
            if (err) {
              reject(err);
            } else {
              resolve(res);
            }
        });
      }).catch((err) => {
            throw err;
      });
}